<template>
  <div class="user-space-container">
    <!-- Main Layout Wrapper -->
    <div class="user-space-layout-wrapper" v-if="user">
      <!-- Banner Section -->
      <div class="space-banner">
        <div class="banner-overlay"></div>
      </div>

      <div class="user-space-main-body">
        <!-- Left Sidebar -->
        <div class="sidebar glass-card">
          <div class="avatar-section">
            <Avatar :src="user.avatar || 'https://cube.elemecdn.com/3/7c/3ea6beec64369c2642b92c6726f1epng.png'"
              class="profile-avatar" shape="circle" />
          </div>

          <div class="name-section">
            <h2 class="username-title">{{ user.username }}</h2>
            <div class="user-bio-wrapper">
              <p class="user-bio" v-if="user.bio">{{ user.bio }}</p>
              <p class="user-bio empty-bio" v-else-if="isSelf" @click="$router.push('/profile')">
                点击编辑个人简介，让大家认识你...
              </p>
              <p class="user-bio empty-bio" v-else>
                该用户很懒，还没有填写简介~
              </p>
            </div>
            <div class="user-tags" v-if="user.subject || user.grade">
              <Tag color="cyan" ghost v-if="user.subject">{{ user.subject }}</Tag>
              <Tag color="blue" ghost v-if="user.grade">{{ user.grade }}</Tag>
            </div>
          </div>

          <div class="stats-section">
            <div class="stat-item">
              <div class="stat-label">获赞</div>
              <div class="stat-value">{{ formatNumber(stats.likes || 0) }}</div>
            </div>
            <div class="stat-item">
              <div class="stat-label">粉丝</div>
              <div class="stat-value">{{ formatNumber(stats.followers || 0) }}</div>
            </div>
            <div class="stat-item">
              <div class="stat-label">阅读</div>
              <div class="stat-value">{{ formatNumber(stats.views || 0) }}</div>
            </div>
          </div>

          <div class="achievement-section">
            <Divider size="small">创作成果</Divider>
            <div class="achievement-grid">
              <div class="achieve-item">
                <Icon type="ios-paper" size="18" color="#3b82f6" />
                <span class="achieve-val">{{ stats.papers || 0 }}</span>
                <span class="achieve-label">试卷</span>
              </div>
              <div class="achieve-item">
                <Icon type="md-podium" size="18" color="#10b981" />
                <span class="achieve-val">{{ stats.posts || 0 }}</span>
                <span class="achieve-label">动态</span>
              </div>
            </div>
          </div>

          <div class="actions-section" v-if="!isSelf">
            <Button :type="isFollowing ? 'default' : 'primary'" class="action-btn follow-btn" @click="toggleFollow"
              :loading="followLoading" round>
              {{ isFollowing ? '已关注' : '关注' }}
            </Button>
            <Button class="action-btn message-btn" @click="startChat" :loading="chatLoading" ghost>
              发消息
            </Button>
          </div>

          <div class="meta-section">
            <span class="meta-tag">
              <Icon type="ios-pin" /> {{ user.school || '未知学校' }}
            </span>
          </div>
        </div>

        <!-- Main Content Area -->
        <div class="main-content glass-card">
          <div class="custom-tabs">
            <div class="tab-header">
              <div class="tab-item" :class="{ active: activeTab === 'dynamic' }" @click="activeTab = 'dynamic'">动态</div>
              <div class="tab-item" :class="{ active: activeTab === 'courses' }" @click="activeTab = 'courses'">课程</div>
              <div class="tab-item" :class="{ active: activeTab === 'more' }" @click="activeTab = 'more'">更多</div>
            </div>
            <div class="tab-content">
              <div v-show="activeTab === 'dynamic'">
                <div class="posts-list" v-if="posts.length > 0">
                  <div class="post-card dynamic-card" v-for="post in posts" :key="post.id" @click="goToPost(post.id)">
                    <div class="post-header">
                      <div class="type-tag-wrapper">
                        <span class="post-tag">{{ post.type === 'question' ? '提问' : '分享' }}</span>
                        <span class="original-tag">原文</span>
                      </div>
                      <Icon type="ios-more" size="20" class="more-icon-btn" @click.stop />
                    </div>
                    <div class="post-author-row">
                      <Avatar :src="post.author.avatar || defaultAvatar" size="small" />
                      <div class="author-info">
                        <span class="author-name">{{ post.author.username }}</span>
                        <span class="post-meta-line">{{ formatTime(post.createdAt) }} · {{ post.author.school || '未知地域'
                        }}</span>
                      </div>
                    </div>
                    <div class="post-body">
                      <p class="post-text">{{ post.content }}</p>
                      <div v-if="post.images && post.images.length" class="post-images-grid">
                        <div v-for="(img, idx) in post.images.slice(0, 3)" :key="idx" class="img-item">
                          <img :src="img" alt="dynamic-img" />
                        </div>
                      </div>
                    </div>
                    <div class="post-actions" @click.stop>
                      <div class="action-item like-btn" @click="handleLike(post)">
                        <Icon :type="post.liked ? 'md-thumbs-up' : 'ios-thumbs-up-outline'"
                          :class="{ active: post.liked }" />
                        <span>{{ post.likes || 0 }}</span>
                      </div>
                      <div class="action-item">
                        <Icon type="ios-chatbubbles-outline" />
                        <span>{{ post.comments ? post.comments.length : 0 }}</span>
                      </div>
                      <div class="action-item">
                        <Icon type="ios-star-outline" />
                        <span>收藏</span>
                      </div>
                    </div>
                  </div>
                  <div class="load-more-section">
                    <template v-if="hasMore">
                      <Spin size="large" v-if="loadingMore"></Spin>
                      <div class="loading-trigger-text" v-else v-intersect="onIntersect">
                        <Icon type="ios-arrow-dropdown" size="20" class="bounce-icon" />
                        <span>正在探索更多精彩动态...</span>
                      </div>
                    </template>
                    <div class="no-more-content" v-else-if="posts.length > 0">
                      <Divider><span class="no-more-text">到底啦，探索更多其他空间吧 ~</span></Divider>
                    </div>
                  </div>
                </div>
                <div v-else class="empty-state">
                  <Icon type="ios-albums-outline" size="64" color="#ccc" />
                  <p>该用户还没有发布任何动态</p>
                </div>
              </div>
              <div v-show="activeTab === 'courses'">
                <div class="activities-list" v-if="activities.length > 0">
                  <div class="activity-card" v-for="activity in activities" :key="activity.id"
                    @click="$router.push(`/activities/${activity.id}`)">
                    <div class="activity-cover">
                      <img :src="activity.coverImage || defaultCover" alt="activity-cover">
                      <div class="activity-tag">{{ activity.enrollCount }} 人参与</div>
                    </div>
                    <div class="activity-info">
                      <h3 class="activity-title">{{ activity.title }}</h3>
                      <p class="activity-desc">{{ activity.description }}</p>
                      <div class="activity-footer">
                        <span class="activity-time">{{ formatDate(activity.createdAt) }} 开启</span>
                        <Button type="primary" size="small" ghost round>进入学习</Button>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-else class="empty-state">
                  <Icon type="ios-school-outline" size="64" color="#ccc" />
                  <p>该用户还没有参加任何课程</p>
                </div>
              </div>
              <div v-show="activeTab === 'more'">
                <div class="empty-state">
                  <p>更多内容建设中...</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else-if="loading" class="loading-state">
      <Spin size="large"></Spin>
    </div>
    <div v-else class="error-state">
      <h2>用户不存在或已注销</h2>
      <Button type="primary" @click="$router.push('/forum')">返回论坛</Button>
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/zh-cn';

dayjs.extend(relativeTime);
dayjs.locale('zh-cn');

export default {
  name: 'UserSpace',
  directives: {
    intersect: {
      mounted(el, binding) {
        const options = {
          root: null,
          threshold: 0.1
        };
        const observer = new IntersectionObserver((entries) => {
          if (!el.isConnected) return;
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              binding.value();
            }
          });
        }, options);
        observer.observe(el);
        el._observer = observer;
      },
      unmounted(el) {
        if (el && el._observer) {
          el._observer.disconnect();
          delete el._observer;
        }
      }
    }
  },
  data() {
    return {
      userId: null,
      user: null,
      stats: {
        followers: 0,
        following: 0,
        views: 0,
        likes: 0,
        posts: 0,
        papers: 0
      },
      posts: [],
      activities: [],

      activeTab: 'dynamic',
      loading: true,
      loadingMore: false,
      page: 1,
      pageSize: 10,
      hasMore: false,
      isFollowing: false,
      followLoading: false,
      chatLoading: false,
      defaultAvatar: 'https://cube.elemecdn.com/3/7c/3ea6beec64369c2642b92c6726f1epng.png',
      defaultCover: 'https://images.unsplash.com/photo-1546410531-bb4caa6b424d?auto=format&fit=crop&q=80&w=1000'
    };
  },
  computed: {
    currentUser() {
      return this.$store.state.user;
    },
    isSelf() {
      if (!this.currentUser) return false;
      return parseInt(this.userId) === this.currentUser.id;
    }
  },
  created() {
    this.userId = this.$route.params.id;
    this.fetchUserSpace();
    if (this.currentUser && !this.isSelf) {
      this.checkFollowStatus();
    }
  },
  watch: {
    '$route.params.id'(newId) {
      if (newId) {
        this.userId = newId;
        this.fetchUserSpace();
        if (this.currentUser && !this.isSelf) {
          this.checkFollowStatus();
        }
      }
    }
  },
  methods: {
    async fetchUserSpace(isLoadMore = false) {
      if (isLoadMore && this.loadingMore) return;

      if (isLoadMore) {
        this.loadingMore = true;
      } else {
        this.loading = true;
        this.page = 1;
        this.posts = [];
      }

      try {
        const res = await fetch(`/api/user/${this.userId}/space?page=${this.page}&page_size=${this.pageSize}`, {
          headers: this.getHeaders()
        });
        if (res.ok) {
          const data = await res.json();
          if (isLoadMore) {
            this.posts = [...this.posts, ...(data.posts || [])];
          } else {
            this.user = data.user;
            this.stats = data.stats;
            this.posts = data.posts || [];
            this.activities = data.activities || [];
          }
          this.hasMore = data.hasMore;
        } else {
          this.$Message.error('获取用户空间失败');
          if (!isLoadMore) this.user = null;
        }
      } catch (e) {
        this.$Message.error('网络错误');
        if (!isLoadMore) this.user = null;
      } finally {
        this.loading = false;
        this.loadingMore = false;
      }
    },
    onIntersect() {
      if (this.hasMore && !this.loadingMore && !this.loading) {
        this.page += 1;
        this.fetchUserSpace(true);
      }
    },
    async checkFollowStatus() {
      try {
        const res = await fetch(`/api/user/${this.userId}/is_following`, {
          headers: this.getHeaders()
        });
        if (res.ok) {
          const data = await res.json();
          this.isFollowing = data.isFollowing;
        }
      } catch (e) {
        console.error('Check follow status error:', e);
      }
    },
    async toggleFollow() {
      if (!this.currentUser) {
        this.$Message.warning('请先登录');
        this.$router.push({ name: 'Login', query: { redirect: this.$route.fullPath } });
        return;
      }
      this.followLoading = true;
      try {
        const res = await fetch(`/api/user/${this.userId}/follow`, {
          method: 'POST',
          headers: this.getHeaders()
        });
        if (res.ok) {
          const data = await res.json();
          this.isFollowing = data.isFollowing;
          this.$Message.success(data.message);
          // Update stats locally to avoid refetch
          if (this.isFollowing) {
            this.stats.followers += 1;
          } else {
            this.stats.followers -= 1;
          }
        } else {
          const errorData = await res.json();
          this.$Message.error(errorData.error || '操作失败');
        }
      } catch (e) {
        this.$Message.error('网络错误');
      } finally {
        this.followLoading = false;
      }
    },
    async startChat() {
      if (!this.currentUser) {
        this.$Message.warning('请先登录');
        this.$router.push({ name: 'Login', query: { redirect: this.$route.fullPath } });
        return;
      }

      this.chatLoading = true;
      try {
        const res = await fetch('/api/chat/rooms', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.$store.state.token}`
          },
          body: JSON.stringify({
            targetUserId: parseInt(this.userId),
            type: 'private'
          })
        });

        if (res.ok) {
          this.$router.push('/chat');
        } else {
          const data = await res.json();
          this.$Message.error(data.error || '发起聊天失败');
        }
      } catch (e) {
        this.$Message.error('网络错误');
      } finally {
        this.chatLoading = false;
      }
    },
    goToPost(postId) {
      this.$router.push(`/forum/${postId}`);
    },
    getHeaders() {
      const headers = {};
      if (this.$store.state.token) {
        headers['Authorization'] = `Bearer ${this.$store.state.token}`;
      }
      return headers;
    },
    async handleLike(post) {
      if (!this.currentUser) {
        this.$Message.warning('请先登录');
        return;
      }
      try {
        const res = await fetch(`/api/forum/posts/${post.id}/like`, {
          method: 'POST',
          headers: this.getHeaders()
        });
        if (res.ok) {
          const data = await res.json();
          post.liked = data.liked;
          post.likes = data.likes;

          // If this is the user's space, update the total likes in sidebar
          if (parseInt(this.userId) === post.authorId) {
            this.stats.likes = data.liked ? (this.stats.likes + 1) : (this.stats.likes - 1);
          }
        } else {
          this.$Message.error('点赞失败');
        }
      } catch (e) {
        this.$Message.error('网络错误');
      }
    },
    formatNumber(num) {
      if (num >= 10000) {
        return (num / 10000).toFixed(1) + '万';
      }
      return num;
    },
    formatTime(dateString) {
      if (!dateString) return '';
      return dayjs(dateString).fromNow();
    },
    formatDate(timestamp) {
      if (!timestamp) return '';
      return dayjs(timestamp * 1000).format('YYYY-MM-DD');
    }
  }
};
</script>

<style scoped>
.user-space-container {
  min-height: calc(100vh - 64px);
  background: #f0f2f5 url('../assets/logo.png') no-repeat center center fixed;
  background-size: cover;
  display: flex;
  justify-content: center;
  position: relative;
}

.user-space-container::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(240, 242, 245, 0.85);
  z-index: 0;
}

.user-space-layout-wrapper {
  position: relative;
  z-index: 1;
  width: 1100px;
  max-width: 95%;
  margin: 24px 0;
}

.space-banner {
  width: 100%;
  height: 240px;
  background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%);
  border-radius: 12px 12px 0 0;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.banner-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to bottom, transparent 0%, rgba(0, 0, 0, 0.3) 100%);
}

.user-space-main-body {
  display: flex;
  gap: 20px;
  margin-top: -60px;
  /* Overlap with banner */
  padding: 0 24px;
  align-items: flex-start;
}

.sidebar {
  width: 300px;
  flex-shrink: 0;
}

.main-content {
  flex: 1;
  min-height: 700px;
}

.custom-tabs {
  padding: 0;
}

.tab-header {
  display: flex;
  border-bottom: 1px solid #e8e8e8;
  padding: 0 20px;
}

.tab-item {
  padding: 12px 20px;
  font-size: 15px;
  color: #808695;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: all 0.3s ease;
  font-weight: 500;
}

.tab-item:hover {
  color: #2d8cf0;
}

.tab-item.active {
  color: #2d8cf0;
  border-bottom-color: #2d8cf0;
}

.tab-content {
  padding: 16px 20px;
}

.glass-card {
  background: rgba(255, 255, 255, 0.75);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(31, 38, 135, 0.1);
}

.avatar-section {
  padding: 30px 0 10px;
  display: flex;
  justify-content: center;
}

.profile-avatar {
  width: 120px;
  height: 120px;
  border: 5px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.name-section {
  padding: 10px 24px 20px;
  text-align: center;
}

.username-title {
  font-size: 24px;
  font-weight: 800;
  color: #1a1a1a;
  margin-bottom: 12px;
  letter-spacing: -0.5px;
}

.user-bio-wrapper {
  margin-bottom: 16px;
  min-height: 20px;
}

.user-bio {
  font-size: 14px;
  color: #4b5563;
  line-height: 1.6;
  max-width: 240px;
  margin: 0 auto;
}

.empty-bio {
  color: #9ca3af;
  font-style: italic;
  font-size: 13px;
  cursor: pointer;
}

.empty-bio:hover {
  color: #3b82f6;
}

.load-more-section {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 0;
  color: #94a3b8;
  font-size: 14px;
  position: relative;
  min-height: 80px;
}

.loading-trigger-text {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  animation: fadeIn 0.5s ease;
}

.bounce-icon {
  animation: bounce 2s infinite;
  color: #3b82f6;
}

@keyframes bounce {

  0%,
  20%,
  50%,
  80%,
  100% {
    transform: translateY(0);
  }

  40% {
    transform: translateY(-10px);
  }

  60% {
    transform: translateY(-5px);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.no-more-text {
  color: #94a3b8;
  font-size: 13px;
  font-style: italic;
  letter-spacing: 0.5px;
}

.no-more-content {
  padding: 30px 0;
  opacity: 0.8;
}

.user-tags {
  display: flex;
  gap: 6px;
  justify-content: center;
  flex-wrap: wrap;
}

.achievement-section {
  padding: 0 24px 20px;
}

.achievement-grid {
  display: flex;
  justify-content: center;
  gap: 30px;
  padding-top: 10px;
}

.achieve-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.achieve-val {
  font-size: 18px;
  font-weight: 700;
  color: #1a1a1a;
}

.achieve-label {
  font-size: 12px;
  color: #9ca3af;
}

.stats-section {
  display: flex;
  justify-content: space-around;
  padding: 24px 10px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.stat-item {
  text-align: center;
}

.stat-label {
  font-size: 12px;
  color: #8590a6;
  margin-bottom: 6px;
}

.stat-value {
  font-size: 18px;
  font-weight: 700;
  color: #1a1a1a;
}

.actions-section {
  padding: 24px 20px;
  display: flex;
  gap: 12px;
}

.action-btn {
  height: 38px;
  font-weight: 600;
  border-radius: 19px;
}

.meta-section {
  padding: 16px 20px 24px;
  font-size: 13px;
  color: #666;
  text-align: center;
}

.meta-section .ivu-icon {
  margin-right: 4px;
  color: #3b82f6;
}

/* Tab Styling Overrides */
:deep(.ivu-tabs-nav-container) {
  font-size: 16px;
}

:deep(.ivu-tabs-ink-bar) {
  height: 3px;
  border-radius: 3px;
}

/* Dynamic Card Upgrade */
.dynamic-card {
  margin: 20px 0;
  background: rgba(255, 255, 255, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 10px;
  padding: 24px;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  cursor: pointer;
}

.dynamic-card:hover {
  transform: translateY(-4px);
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.08);
}

.type-tag-wrapper {
  display: flex;
  align-items: center;
  gap: 10px;
}

.post-tag {
  color: #3b82f6;
  background: rgba(59, 130, 246, 0.1);
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
}

.original-tag {
  font-size: 11px;
  color: #999;
  background: #f5f5f5;
  padding: 1px 6px;
  border-radius: 2px;
}

.more-icon-btn {
  color: #999;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: background 0.2s;
}

.more-icon-btn:hover {
  background: #f0f0f0;
  color: #333;
}

.post-author-row {
  margin: 16px 0;
  display: flex;
  align-items: center;
  gap: 12px;
}

.author-name {
  font-size: 15px;
  font-weight: 600;
  color: #222;
}

.post-meta-line {
  font-size: 12px;
  color: #999;
  margin-top: 2px;
}

.post-text {
  font-size: 15px;
  color: #333;
  line-height: 1.6;
  margin-bottom: 16px;
}

.post-images-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 8px;
  margin-bottom: 16px;
}

.img-item {
  aspect-ratio: 4/3;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #eee;
}

.img-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s;
}

.dynamic-card:hover .img-item img {
  transform: scale(1.05);
}

.post-actions {
  display: flex;
  gap: 32px;
  padding-top: 16px;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
}

.action-item {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #8590a6;
  font-size: 14px;
  transition: color 0.2s;
}

.action-item:hover {
  color: #3b82f6;
}

.action-item .ivu-icon {
  font-size: 18px;
}

.like-btn .active {
  color: #ed4014;
  animation: like-pulse 0.4s ease-out;
}

@keyframes like-pulse {
  0% {
    transform: scale(1);
  }

  50% {
    transform: scale(1.3);
  }

  100% {
    transform: scale(1);
  }
}

.empty-state {
  padding: 80px 0;
  text-align: center;
}

.empty-state p {
  color: #999;
  margin-top: 16px;
  font-size: 15px;
}

.loading-state {
  height: 500px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.activities-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.activity-card {
  display: flex;
  background: #fff;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: all 0.3s ease;
  cursor: pointer;
  border: 1px solid #f0f0f0;
}

.activity-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  border-color: #2d8cf0;
}

.activity-cover {
  width: 140px;
  min-height: 100px;
  position: relative;
  overflow: hidden;
  flex-shrink: 0;
}

.activity-cover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.activity-tag {
  position: absolute;
  bottom: 6px;
  left: 6px;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  padding: 1px 6px;
  border-radius: 3px;
  font-size: 11px;
}

.activity-info {
  padding: 12px 16px;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
}

.activity-title {
  font-size: 15px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.activity-desc {
  font-size: 12px;
  color: #64748b;
  margin-bottom: 8px;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.activity-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.activity-time {
  font-size: 11px;
  color: #94a3b8;
}
</style>
